import React, { useState } from 'react'

export default function VanSaleForm({ sale, onClose }) {
  const [formData, setFormData] = useState({
    sale_date: sale?.sale_date || '',
    sale_type: sale?.sale_type || 'customer_upfit',
    sale_price: sale?.sale_price || '',
    initial_van_cost: sale?.initial_van_cost || '',
    serial_number: sale?.serial_number || '',
    vin: sale?.vin || ''
  })
  const [errors, setErrors] = useState([])
  const [submitting, setSubmitting] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setErrors([])
    setSubmitting(true)

    const url = sale ? `/api/van_sales/${sale.id}` : '/api/van_sales'
    const method = sale ? 'PATCH' : 'POST'

    fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ van_sale: formData })
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(data => {
            throw new Error(data.errors?.join(', ') || 'Failed to save van sale')
          })
        }
        return response.json()
      })
      .then(() => {
        onClose()
      })
      .catch(err => {
        setErrors([err.message])
        setSubmitting(false)
      })
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {sale ? 'Edit Van Sale' : 'Add Van Sale'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        {errors.length > 0 && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
            <ul className="list-disc list-inside text-sm text-red-600">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="sale_date" className="block text-sm font-medium text-gray-700">
                Sale Date *
              </label>
              <input
                type="date"
                id="sale_date"
                name="sale_date"
                value={formData.sale_date}
                onChange={handleChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900"
              />
            </div>

            <div>
              <label htmlFor="sale_type" className="block text-sm font-medium text-gray-700">
                Sale Type *
              </label>
              <select
                id="sale_type"
                name="sale_type"
                value={formData.sale_type}
                onChange={handleChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900"
              >
                <option value="customer_upfit">Customer Upfit</option>
                <option value="van_sale">Van Sale</option>
              </select>
            </div>

            <div>
              <label htmlFor="sale_price" className="block text-sm font-medium text-gray-700">
                Sale Price *
              </label>
              <input
                type="number"
                id="sale_price"
                name="sale_price"
                value={formData.sale_price}
                onChange={handleChange}
                step="0.01"
                min="0"
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900"
              />
            </div>

            {formData.sale_type === 'van_sale' && (
              <div>
                <label htmlFor="initial_van_cost" className="block text-sm font-medium text-gray-700">
                  Initial Van Cost
                </label>
                <input
                  type="number"
                  id="initial_van_cost"
                  name="initial_van_cost"
                  value={formData.initial_van_cost}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900"
                />
              </div>
            )}

            <div>
              <label htmlFor="serial_number" className="block text-sm font-medium text-gray-700">
                Serial Number
                <span className="text-xs text-gray-500 ml-1">(format: #0000)</span>
              </label>
              <input
                type="text"
                id="serial_number"
                name="serial_number"
                value={formData.serial_number}
                onChange={handleChange}
                placeholder="#0136"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900"
              />
            </div>

            <div>
              <label htmlFor="vin" className="block text-sm font-medium text-gray-700">
                VIN
              </label>
              <input
                type="text"
                id="vin"
                name="vin"
                value={formData.vin}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
            >
              {submitting ? 'Saving...' : (sale ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
